import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';

import {showLoading, updateLoading} from '@/actions/loadingActions';
import {GetRequest} from '@/api/request';
import {GET_CYCLE_HISTORY_LIST} from '@/constants/apis';
import {REGEX_CONICAL_15, REGEX_CONICAL_50} from '@/constants/constant';
import {
  HISTORY_SHOW_TYPE,
  HISTORY_SHOW_TYPE_DATE_OPTION,
  CYCLE_LOG_PROFILE_MODE,
  PROFILE_MODE,
  PROFILE_TYPE,
  OPERATION_COMPLETE_STATUS,
} from '@/constants/enums';
import {PROFILE_HISTORY} from '@/constants/messages';
// import useIsMounted from '@/hooks/useIsMounted';
// import {consoleLog} from '@/libs/consoleHelper';
import {sortByKeyWithRegex} from '@/libs/arrayHelper';
import {DateToString, DateToYMD, ParseToDate} from '@/libs/dateHelper';

// TODO it may a good option to move it when remote view store was available since it only to storing isRemoteView state
const CycleHistoryRemoteViewContext = createContext(null);
const CycleHistoryContext = createContext(null);
const CycleHistoryProfileModeContext = createContext(null);
const CycleHistoryFilterContext = createContext(null);
const CycleHistoryFilterBeginContext = createContext(null); // New Profile History
const CycleHistoryFilterEndContext = createContext(null); // New Profile History
const CycleHistoryFilteredContext = createContext(null);

const getInitHistoryState = (value = '', obj = {}, initVal = '') => {
  let result = initVal;
  if (value && value !== 'null' && Object.values(obj).includes(value)) result = value;

  return result;
};

/**
 *
 * @param {Boolean} isRemoteView
 * @param {Object} param1
 * @returns
 */
export function CycleHistoryContextProvider({isRemoteView, children}) {
  const dispatch = useDispatch();
  // TODO should set enum for local storage item name
  const [showType, SetShowType] = useState(HISTORY_SHOW_TYPE.ALL);
  const [showOrder, SetShowOrder] = useState(HISTORY_SHOW_TYPE_DATE_OPTION.NEWER);
  const [availableCycleHistoryList, setAvailableCycleHistoryList] = useState({
    value: [],
    hasAllData: false,
  });
  const [profileMode, SetProfileMode] = useState(CYCLE_LOG_PROFILE_MODE.ALL);
  const [searchFilter, SetSearchFilter] = useState(new Date());
  const [searchFilterBegin, SetSearchFilterBegin] = useState(() => {
    const beginDate = new Date();
    beginDate.setHours(0, 0, 0);
    return beginDate;
  }); // New Profile History
  const [searchFilterEnd, SetSearchFilterEnd] = useState(() => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59);
    return endDate;
  }); // New Profile History
  const [selectAll, SetSelectAll] = useState(false); // New Profile History
  // const isMounted = useIsMounted();

  const dataCycleHistoryLog = useMemo(
    () => ({
      params: {
        dateLog: `${DateToYMD(searchFilterBegin)}-${DateToYMD(searchFilterEnd)}`,
        historyListType: getInitHistoryState(showType, HISTORY_SHOW_TYPE, HISTORY_SHOW_TYPE.DATE),
        nextId: -1,
        order: getInitHistoryState(showOrder, HISTORY_SHOW_TYPE_DATE_OPTION, HISTORY_SHOW_TYPE_DATE_OPTION.NEWER),
      },
    }),
    [searchFilterBegin, searchFilterEnd, showType, showOrder],
  );

  const GetCycleLog = useCallback(async () => {
    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: PROFILE_HISTORY.GET_HISTORY_LIST_DATA,
      }),
    );

    await GetRequest(GET_CYCLE_HISTORY_LIST, dataCycleHistoryLog)
      .then((response) => {
        const {FileList} = response.data;
        setAvailableCycleHistoryList({
          value: [...FileList.map((file) => FormatFileResponse(file))],
          hasAllData: showType === HISTORY_SHOW_TYPE.ALL,
        });
      })
      .finally(() => dispatch(showLoading(false)));
  }, [dataCycleHistoryLog, dispatch, showType]);

  useEffect(() => {
    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: PROFILE_HISTORY.GET_HISTORY_LIST_DATA,
      }),
    );

    SetShowType(dataCycleHistoryLog.params.historyListType);
    SetShowOrder(dataCycleHistoryLog.params.order);

    if (!availableCycleHistoryList.hasAllData) GetCycleLog();
    else dispatch(showLoading(false));

    return () => {
      if (showType) window.localStorage.setItem('ProfileHistoryShowType', showType);
      if (showOrder) window.localStorage.setItem('ProfileHistoryShowOrder', showOrder);
    };
  }, [
    searchFilter,
    showType,
    showOrder,
    dispatch,
    availableCycleHistoryList.hasAllData,
    dataCycleHistoryLog.params.historyListType,
    dataCycleHistoryLog.params.order,
    GetCycleLog,
  ]);

  /**
   * Control displayed data based on date and profile mode
   */
  const filteredCycleHistoryList = useMemo(() => {
    const {value: data} = availableCycleHistoryList;
    let value = [...data];
    let val = [];

    if (value.length > 0) {
      if (showOrder === HISTORY_SHOW_TYPE_DATE_OPTION.NEWER) {
        value.sort((a, b) => b.Date - a.Date);
        val = value;
      } else if (showOrder === HISTORY_SHOW_TYPE_DATE_OPTION.OLDER) {
        value.sort((a, b) => a.Date - b.Date);
        val = value;
      } else if (showOrder === HISTORY_SHOW_TYPE_DATE_OPTION.SORT_TO_50ML) {
        value = sortByKeyWithRegex(value, 'FileName', REGEX_CONICAL_50);
        val = value;
      } else if (showOrder === HISTORY_SHOW_TYPE_DATE_OPTION.SORT_TO_15ML) {
        value = sortByKeyWithRegex(value, 'FileName', REGEX_CONICAL_15);
        val = value;
      } else {
        value.sort((a, b) => (moment(a.Date).isBefore(b.Date) ? 1 : -1));
        val = value;
      }

      return val.filter((cycleLog) => {
        cycleLog.Name = `${DateToString(cycleLog.Date)} ${cycleLog.FileName.replace('.log', '').replace(/_/g, ':')}`;

        const profileModeFilter =
          profileMode === CYCLE_LOG_PROFILE_MODE.ALL ? true : cycleLog.Name.includes(profileMode);
        return showType === HISTORY_SHOW_TYPE.DATE
          ? moment(cycleLog.Date).isSameOrAfter(searchFilterBegin) &&
              moment(cycleLog.Date).isSameOrBefore(searchFilterEnd) &&
              profileModeFilter
          : true && profileModeFilter;
      });
    }

    return val.filter((cycleLog) => {
      cycleLog.Name = `${DateToString(cycleLog.Date)} ${cycleLog.FileName.replace('.log', '').replace(/_/g, ':')}`;

      const profileModeFilter = profileMode === CYCLE_LOG_PROFILE_MODE.ALL ? true : cycleLog.Name.includes(profileMode);
      return showType === HISTORY_SHOW_TYPE.DATE
        ? moment(cycleLog.Date).isSameOrAfter(searchFilterBegin) &&
            moment(cycleLog.Date).isSameOrBefore(searchFilterEnd) &&
            profileModeFilter
        : true && profileModeFilter;
    });
  }, [availableCycleHistoryList, searchFilterBegin, searchFilterEnd, showType, showOrder, profileMode]);

  const selectedCycleHistoryList = useMemo(() => {
    const {value} = availableCycleHistoryList;
    return value.map((item) => ({...item, Active: selectAll}));
  }, [selectAll]);
  /**
   * @param {Object} data
   * @returns {Object}
   */
  const FormatFileResponse = (data) => {
    const date = ParseToDate(data.Date);
    const fileName = data.Name.replace(/ /g, '').replace('.log', '').replace(/_/g, '_').split('-');
    let exportFileName = `µPULSE_${data.Name.replace(' ', '').replace('.log', '.csv')}`;

    const profileMode = data.Name.toLowerCase().includes(PROFILE_MODE.AUTO.toLowerCase())
      ? PROFILE_MODE.AUTO
      : PROFILE_MODE.MANUAL;
    let profileType = PROFILE_TYPE.NONE;
    let profileStatus = OPERATION_COMPLETE_STATUS.NONE;
    if (fileName.length === 2) {
      exportFileName = `µPULSE_${fileName[1]}_${DateToString(date).replace(/\//g, '-')}_${fileName[0].replace(
        /_/g,
        '-',
      )}.csv`;
      if (data.Name.toLowerCase().includes(PROFILE_TYPE.CONCENTRATE.toLowerCase())) {
        profileType = PROFILE_TYPE.CONCENTRATE;
      } else if (data.Name.toLowerCase().includes(PROFILE_TYPE.CONCENTRATE_50.toLowerCase())) {
        profileType = PROFILE_TYPE.CONCENTRATE_50;
      } else if (data.Name.toLowerCase().includes(PROFILE_TYPE.BUFFER_EXCHANGE_CONTINUOUS.toLowerCase())) {
        profileType = PROFILE_TYPE.BUFFER_EXCHANGE_CONTINUOUS;
      } else if (data.Name.toLowerCase().includes(PROFILE_TYPE.BUFFER_EXCHANGE_DISCRETE.toLowerCase())) {
        profileType = PROFILE_TYPE.BUFFER_EXCHANGE_DISCRETE;
      } else {
        profileType = PROFILE_TYPE.NONE;
      }
      if (data.Name.toLowerCase().includes(OPERATION_COMPLETE_STATUS.FINISHED.toLowerCase())) {
        profileStatus = OPERATION_COMPLETE_STATUS.FINISHED;
      } else if (data.Name.toLowerCase().includes(OPERATION_COMPLETE_STATUS.STOPPED.toLowerCase())) {
        profileStatus = OPERATION_COMPLETE_STATUS.STOPPED;
      } else if (data.Name.toLowerCase().includes(OPERATION_COMPLETE_STATUS.ERROR.toLowerCase())) {
        profileStatus = OPERATION_COMPLETE_STATUS.ERROR;
      } else {
        profileStatus = OPERATION_COMPLETE_STATUS.NONE;
      }
    }

    return {
      Active: false,
      Id: data.Name,
      FileName: data.Name.replace('.log', '').replace(/_/g, ':'),
      ExportFileName: exportFileName,
      Date: date,
      Path: data.Path,
      Profile: profileMode,
      Type: profileType,
      Status: profileStatus,
    };
  };

  const CycleHistoryRemoteViewContextValue = useMemo(
    () => ({
      isRemoteView,
    }),
    [],
  );

  const CycleHistoryContextValue = useMemo(
    () => ({
      cycleHistoryList: availableCycleHistoryList,
    }),
    [availableCycleHistoryList],
  );

  const CycleHistoryProfileModeContextValue = useMemo(
    () => ({
      profileMode,
      SetProfileMode,
      selectAll,
      SetSelectAll,
    }),
    [profileMode, selectAll],
  );

  const CycleHistoryFilterContextValue = useMemo(
    () => ({
      showType,
      showOrder,
      searchFilter,
      searchFilterBegin,
      searchFilterEnd,
      SetShowType,
      SetShowOrder,
      SetSearchFilter,
      SetSearchFilterBegin,
      SetSearchFilterEnd,
    }),
    [showType, showOrder, searchFilter, searchFilterBegin, searchFilterEnd],
  );

  const CycleHistoryFilteredContextValue = useMemo(
    () => ({
      filteredCycleHistoryList,
      selectedCycleHistoryList,
    }),
    [filteredCycleHistoryList, selectedCycleHistoryList],
  );

  return (
    <CycleHistoryRemoteViewContext.Provider value={CycleHistoryRemoteViewContextValue}>
      <CycleHistoryContext.Provider value={CycleHistoryContextValue}>
        <CycleHistoryProfileModeContext.Provider value={CycleHistoryProfileModeContextValue}>
          <CycleHistoryFilterContext.Provider value={CycleHistoryFilterContextValue}>
            <CycleHistoryFilteredContext.Provider value={CycleHistoryFilteredContextValue}>
              {children}
            </CycleHistoryFilteredContext.Provider>
          </CycleHistoryFilterContext.Provider>
        </CycleHistoryProfileModeContext.Provider>
      </CycleHistoryContext.Provider>
    </CycleHistoryRemoteViewContext.Provider>
  );
}

CycleHistoryContextProvider.propTypes = {
  isRemoteView: PropTypes.bool,
  children: PropTypes.any,
};

CycleHistoryContextProvider.defaultProps = {
  isRemoteView: false,
  children: null,
};

export const useCycleHistoryRemoteViewContext = () => useContext(CycleHistoryRemoteViewContext);
export const useCycleHistoryContext = () => useContext(CycleHistoryContext);
export const useCycleHistoryProfileModeContext = () => useContext(CycleHistoryProfileModeContext);
export const useCycleHistoryFilterContext = () => useContext(CycleHistoryFilterContext);
export const useCycleHistoryFilterBeginContext = () => useContext(CycleHistoryFilterBeginContext);
export const useCycleHistoryFilterEndContext = () => useContext(CycleHistoryFilterEndContext);
export const useCycleHistoryFilteredContext = () => useContext(CycleHistoryFilteredContext);
